import AvgButton from './ComControl/AvgButton'
import AvgButtonIndex from './ComControl/AvgButtonIndex'
import CustomUIData from './CustomUI/CustomUIData'
import CustomUIGoodItem from './CustomUI/CustomUIGoodItem'
import CustomUIImageStyle from './CustomUI/CustomUIImageStyle'
import PersonFavourItem from './CustomUI/PersonFavourItem'
import EventBase from './EventClass/EventBase'
import EventComment from './EventClass/EventComment'
import { createEmptyEventByCode } from './EventClass/eventFromReader'
import FloatButtonData from './FloatButton/FloatButtonData'
import FloatButtonElementData from './FloatButton/FloatButtonElementData'
import BGMItem from './GameSystemData/BGMData/BGMItem'
import CGItem from './GameSystemData/CGData/CGItem'
import FontSetting from './GameSystemData/FontSetting'
import PersonSetting from './GameSystemData/PersonSetting'
import CustomUIItem from './CustomUI/CustomUIItem'
import Hashtable from './Hashtable'

type Constructor<T = any> = new () => T

type HydrateContext = {
  stack: WeakSet<object>
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function normalizeKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, '')
}

function normalizePath(path: string[]): string {
  return path.map(normalizeKey).join('.')
}

function resolveTargetKey(target: Record<string, unknown>, sourceKey: string): string | undefined {
  if (sourceKey in target) {
    return sourceKey
  }
  const normalizedSourceKey = normalizeKey(sourceKey)
  return Object.keys(target).find((key) => normalizeKey(key) === normalizedSourceKey)
}

function coercePrimitive(current: unknown, source: unknown): unknown {
  if (typeof current === 'number') {
    const next = Number(source)
    return Number.isFinite(next) ? next : current
  }
  if (typeof current === 'boolean') {
    if (typeof source === 'boolean') return source
    if (typeof source === 'number') return source !== 0
    if (typeof source === 'string') return source === 'true' || source === '1'
    return current
  }
  if (typeof current === 'bigint') {
    try {
      return BigInt(source as string | number | bigint)
    } catch {
      return current
    }
  }
  return source
}

function cloneTypedArray(source: unknown): Uint8Array | undefined {
  if (source instanceof Uint8Array) {
    return new Uint8Array(source)
  }
  if (Array.isArray(source) && source.every((item) => typeof item === 'number')) {
    return Uint8Array.from(source)
  }
  return undefined
}

function hydrateEvent(source: unknown, context: HydrateContext): EventBase {
  const codeValue = isRecord(source) ? source.code ?? source.Code : undefined
  const code = typeof codeValue === 'number' ? codeValue : Number(codeValue ?? -1)
  const event = Number.isFinite(code) ? createEmptyEventByCode(code) : new EventBase()
  return hydrateIntoInstance(event, source, [], context)
}

function hydrateCommentEvent(source: unknown, context: HydrateContext): EventComment {
  return hydrateIntoInstance(new EventComment(), source, [], context)
}

const ARRAY_ITEM_FACTORIES = new Map<string, (source: unknown, context: HydrateContext) => unknown>([
  ['totalsetting.fontsettinglist', (source, context) => hydrateIntoInstance(new FontSetting(), source, [], context)],
  ['cg.cgs', (source, context) => hydrateIntoInstance(new CGItem(), source, [], context)],
  ['bgm.bgms', (source, context) => hydrateIntoInstance(new BGMItem(), source, [], context)],
  ['title.buttons', (source, context) => hydrateIntoInstance(new AvgButton(), source, [], context)],
  ['title.buttonsex', (source, context) => hydrateIntoInstance(new AvgButtonIndex(), source, [], context)],
  ['gamemenu.buttons', (source, context) => hydrateIntoInstance(new AvgButton(), source, [], context)],
  ['gamemenu.buttonsex', (source, context) => hydrateIntoInstance(new AvgButtonIndex(), source, [], context)],
  ['btnlist.buttons', (source, context) => hydrateIntoInstance(new AvgButton(), source, [], context)],
  ['talk.talkwin.buttons', (source, context) => hydrateIntoInstance(new AvgButton(), source, [], context)],
  ['talk.talkwin.buttonsex', (source, context) => hydrateIntoInstance(new AvgButtonIndex(), source, [], context)],
  ['bubblepersion.personlist', (source, context) => hydrateIntoInstance(new PersonSetting(), source, [], context)],
  ['customui.cuilist', (source, context) => hydrateIntoInstance(new CustomUIData(), source, [], context)],
  ['customui.cuilist.loadevent', hydrateEvent],
  ['customui.cuilist.afterevent', hydrateEvent],
  ['customui.cuilist.controllist', (source, context) => hydrateIntoInstance(new CustomUIItem(), source, [], context)],
  ['customui.cuilist.controllist.eventlist', hydrateEvent],
  ['customui.cuilist.cuigs.goodlist', (source, context) => hydrateIntoInstance(new CustomUIGoodItem(), source, [], context)],
  ['customui.cuilist.psnfavour.faceimagelist', (source, context) =>
    hydrateIntoInstance(new CustomUIImageStyle(), source, [], context)],
  ['customui.cuilist.psnfavour.faceshowlist', (source, context) =>
    hydrateIntoInstance(new CustomUIImageStyle(), source, [], context)],
  ['customui.cuilist.psnfavour.facenamelist', (source, context) =>
    hydrateIntoInstance(new CustomUIImageStyle(), source, [], context)],
  ['customui.cuilist.psnfavour.personfavoutlist', (source, context) =>
    hydrateIntoInstance(new PersonFavourItem(), source, [], context)],
  ['floatbutton.fbtnlist', (source, context) => hydrateIntoInstance(new FloatButtonData(), source, [], context)],
  ['floatbutton.fbtnlist.elementlist', (source, context) =>
    hydrateIntoInstance(new FloatButtonElementData(), source, [], context)],
  ['floatbutton.fbtnlist.eventlist', hydrateEvent],
  ['floatbutton.fbtnlist.elementlist.eventlist', hydrateCommentEvent],
])

function createArrayItemFromSample(sample: unknown, source: unknown, context: HydrateContext): unknown {
  if (sample instanceof Uint8Array) {
    return cloneTypedArray(source) ?? sample
  }
  if (!sample || typeof sample !== 'object' || Array.isArray(sample)) {
    return source
  }
  const Ctor = (sample as { constructor?: Constructor }).constructor
  if (!Ctor || Ctor === Object) {
    return source
  }
  return hydrateIntoInstance(new Ctor(), source, [], context)
}

function hydrateArray(current: unknown[], source: unknown, path: string[], context: HydrateContext): unknown[] {
  if (!Array.isArray(source)) {
    return current
  }

  const factory = ARRAY_ITEM_FACTORIES.get(normalizePath(path))
  if (factory) {
    return source.map((item) => factory(item, context))
  }

  const sample = current[0]
  if (sample !== undefined) {
    return source.map((item) => createArrayItemFromSample(sample, item, context))
  }

  return source.map((item) => item)
}

function hydrateValue(current: unknown, source: unknown, path: string[], context: HydrateContext): unknown {
  if (source === undefined) {
    return current
  }

  if (current instanceof Uint8Array) {
    return cloneTypedArray(source) ?? current
  }

  if (current instanceof Hashtable) {
    if (isRecord(source)) {
      current.Data = { ...source }
    }
    return current
  }

  if (Array.isArray(current)) {
    return hydrateArray(current, source, path, context)
  }

  if (isRecord(current) && isRecord(source)) {
    return hydrateIntoInstance(current, source, path, context)
  }

  return coercePrimitive(current, source)
}

export function hydrateIntoInstance<T>(
  target: T,
  source: unknown,
  path: string[] = [],
  context: HydrateContext = { stack: new WeakSet<object>() },
): T {
  if (!isRecord(source) || !target || typeof target !== 'object') {
    return target
  }

  if (context.stack.has(source)) {
    return target
  }
  context.stack.add(source)

  const targetRecord = target as Record<string, unknown>
  for (const [sourceKey, sourceValue] of Object.entries(source)) {
    const targetKey = resolveTargetKey(targetRecord, sourceKey)
    if (!targetKey) {
      continue
    }
    const nextPath = [...path, targetKey]
    const currentValue = targetRecord[targetKey]
    targetRecord[targetKey] = hydrateValue(currentValue, sourceValue, nextPath, context)
  }

  context.stack.delete(source)
  return target
}

